import React, { useState, useEffect, useRef } from 'react';
import { bool, func, object, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { isStripeError } from '../../util/errors';
import * as validators from '../../util/validators';
import { propTypes } from '../../util/types';
import {
  getBankPayoutCurrencyOptions,
  getPayoutCountryLabel,
  getSortedPayoutCountryCodes,
  hasOperatorStripeOverride,
  isStripePayoutCountry,
} from '../../util/payoutCountries';
import {
  ALTERNATIVE_PAYOUT_METHODS,
  ALTERNATIVE_PAYOUT_METHOD_IDS,
  EMPTY_BANK_ACCOUNT,
  getAlternativePayout,
  isAlternativePayoutConfigured,
  userChoseStripePayout,
} from '../../util/alternativePayout';

import {
  H4,
  Button,
  FieldSearchableSelect,
  FieldSelect,
  FieldRadioButton,
  FieldTextInput,
  Form,
  InlineTextButton,
} from '../../components';
import IconPayoutMethod from '../IconPayoutMethod/IconPayoutMethod';

import AddIcon from '@mui/icons-material/Add';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { SecondaryButton } from '../mui/Button';
import { SWDialog } from '../mui/Dialog';
import layoverCss from '../../styles/layoverDialog.module.css';

import { DialogContent, DialogTitle, IconButton, useMediaQuery } from '@mui/material';

import { useTheme } from '@emotion/react';

import css from './StripeConnectAccountForm.module.css';

const STEP_SETUP = 'setup';
const STEP_STRIPE = 'stripe';
const STEP_ALTERNATIVE = 'alternative';

const PAYOUT_METHOD_MESSAGE_IDS = {
  [ALTERNATIVE_PAYOUT_METHODS.WISE]: 'StripeConnectAccountForm.payoutMethod.wise',
  [ALTERNATIVE_PAYOUT_METHODS.REVOLUT]: 'StripeConnectAccountForm.payoutMethod.revolut',
  [ALTERNATIVE_PAYOUT_METHODS.PAYPAL]: 'StripeConnectAccountForm.payoutMethod.paypal',
  [ALTERNATIVE_PAYOUT_METHODS.BANK_TRANSFER]: 'StripeConnectAccountForm.payoutMethod.bankTransfer',
};

const PAYOUT_METHOD_HANDLE_LABELS = {
  [ALTERNATIVE_PAYOUT_METHODS.WISE]: 'StripeConnectAccountForm.payoutHandle.wiseLabel',
  [ALTERNATIVE_PAYOUT_METHODS.REVOLUT]: 'StripeConnectAccountForm.payoutHandle.revolutLabel',
  [ALTERNATIVE_PAYOUT_METHODS.PAYPAL]: 'StripeConnectAccountForm.payoutHandle.paypalLabel',
};

const PAYOUT_METHOD_HANDLE_PLACEHOLDERS = {
  [ALTERNATIVE_PAYOUT_METHODS.WISE]: 'StripeConnectAccountForm.payoutHandle.wisePlaceholder',
  [ALTERNATIVE_PAYOUT_METHODS.REVOLUT]: 'StripeConnectAccountForm.payoutHandle.revolutPlaceholder',
  [ALTERNATIVE_PAYOUT_METHODS.PAYPAL]: 'StripeConnectAccountForm.payoutHandle.paypalPlaceholder',
};

// Hidden input field
const FieldHidden = props => {
  const { name } = props;
  return (
    <Field id={name} name={name} type="hidden" className={css.unitTypeHidden}>
      {fieldRenderProps => <input {...fieldRenderProps?.input} />}
    </Field>
  );
};

const ContactSupportLink = () => {
  const handleContactSupportClick = event => {
    event.preventDefault();
    // eslint-disable-next-line no-undef
    Tawk_API.toggle();
  };

  return (
    <a href="#" onClick={handleContactSupportClick} className={css.supportLink}>
      <FormattedMessage id="StripeConnectAccountStatusModal.link" />
    </a>
  );
};

const useDialog = () => {
  const [dialog, setDialog] = useState(false);

  const handleClose = () => {
    setDialog(false);
  };

  const createDialogClickHandler = dialogId => {
    return event => {
      event.preventDefault();
      setDialog(dialogId);
    };
  };

  return { dialog, handleClose, createDialogClickHandler };
};

const InfoModal = props => {
  const { onClose, dialog } = props;

  const theme = useTheme();
  const descriptionElementRef = useRef(null);
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const translationValues = {
    b: msgFragment => <strong>{msgFragment}</strong>,
    p: msgFragment => <p>{msgFragment}</p>,
    contactSupportLink: <ContactSupportLink />,
  };
  return (
    <SWDialog
      open={dialog !== false}
      onClose={onClose}
      fullScreen={fullScreen}
      PaperProps={{
        sx: { minWidth: { md: 630 }, maxHeight: '100vh', maxWidth: '800px' },
      }}
      scroll="paper"
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
    >
      {typeof dialog === 'string' && (
        <>
          <div className={css.dialogHeader}>
            <DialogTitle id="scroll-dialog-title">
              <FormattedMessage id={`StripeConnectAccountFormModal.${dialog}Header`} />
            </DialogTitle>
            <IconButton
              aria-label="close"
              onClick={onClose}
              className={css.closeButton}
              sx={themeSx => ({
                position: 'absolute',
                right: 8,
                top: 8,
                color: themeSx.palette.grey[500],
              })}
            >
              <CloseIcon />
            </IconButton>
          </div>
          <DialogContent dividers={false} ref={descriptionElementRef} tabIndex={-1}>
            <div className={css.dialogContent}>
              <FormattedMessage
                id={`StripeConnectAccountFormModal.${dialog}Text`}
                values={{ ...translationValues }}
              />
            </div>
          </DialogContent>
        </>
      )}
    </SWDialog>
  );
};

const SummaryCards = props => {
  const { accountType, country, intl, bankAccountLastDigits } = props;
  return (
    <div className={css.summarySection}>
      <div className={css.summaryCard}>
        <span className={css.summaryLabel}>
          <FormattedMessage id="StripeConnectAccountForm.accountTypeTitle" />
        </span>
        <span className={css.summaryValue}>
          <FormattedMessage id={`StripeConnectAccountForm.accountTypes.${accountType}`} />
        </span>
      </div>
      <div className={css.summaryCard}>
        <span className={css.summaryLabel}>
          <FormattedMessage id="StripeConnectAccountForm.countryLabel" />
        </span>
        <span className={css.summaryValue}>{getPayoutCountryLabel(intl, country)}</span>
      </div>
      {bankAccountLastDigits ? (
        <div className={css.summaryCard}>
          <span className={css.summaryLabel}>
            <FormattedMessage id="StripeConnectAccountForm.bankAccountLabel" />
          </span>
          <span className={css.summaryValue}>•••• {bankAccountLastDigits}</span>
        </div>
      ) : null}
    </div>
  );
};

const PayoutInfoBox = props => {
  const { children, testId } = props;
  return (
    <div className={css.nonStripeBanner} role="status" data-testid={testId}>
      <InfoOutlinedIcon fontSize="small" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
};

const FirstStepPayoutInfo = props => {
  const { stripeCountries } = props;
  return (
    <FormSpy subscription={{ values: true }}>
      {({ values: spyValues }) => {
        const selectedCountryIsStripe = isStripePayoutCountry(spyValues?.country, stripeCountries);
        return (
          <PayoutInfoBox>
            <FormattedMessage
              id={
                selectedCountryIsStripe
                  ? 'StripeConnectAccountForm.restartSetupNoteStripe'
                  : 'StripeConnectAccountForm.restartSetupNote'
              }
            />
          </PayoutInfoBox>
        );
      }}
    </FormSpy>
  );
};

const ResetPayoutConfirmModal = props => {
  const { open, onClose, onConfirm } = props;
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <SWDialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      PaperProps={{ sx: { maxWidth: 520, width: '100%' } }}
      aria-labelledby="reset-payout-title"
    >
      <div className={css.dialogHeader}>
        <DialogTitle id="reset-payout-title">
          <FormattedMessage id="StripeConnectAccountForm.resetConfirmTitle" />
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onClose}
          className={css.closeButton}
          sx={themeSx => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: themeSx.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
      </div>
      <DialogContent>
        <p className={css.resetModalBody}>
          <FormattedMessage id="StripeConnectAccountForm.resetConfirmMessage" />
        </p>
        <div className={classNames(layoverCss.actions, css.resetModalActions)}>
          <SecondaryButton type="button" onClick={onClose} className={layoverCss.secondaryAction}>
            <FormattedMessage id="StripeConnectAccountForm.resetCancel" />
          </SecondaryButton>
          <Button type="button" rootClassName={css.dangerConfirmButton} onClick={onConfirm}>
            <FormattedMessage id="StripeConnectAccountForm.resetConfirmAction" />
          </Button>
        </div>
      </DialogContent>
    </SWDialog>
  );
};

const getCountryOptions = (intl, countryCodes, stripeCountries, markStripeCountries) =>
  getSortedPayoutCountryCodes(intl, countryCodes).map(code => ({
    value: code,
    label: getPayoutCountryLabel(intl, code),
    meta:
      markStripeCountries && isStripePayoutCountry(code, stripeCountries)
        ? intl.formatMessage({ id: 'StripeConnectAccountForm.countryStripeMeta' })
        : undefined,
  }));

const CreateStripeAccountFields = props => {
  const routeConfiguration = useRouteConfiguration();
  const {
    disabled,
    countryLabel,
    showAsRequired,
    formApi,
    values,
    intl,
    currentUserId,
    marketplaceRootURL,
    payoutCountryCodes,
    stripeCountries,
    markStripeCountries,
    defaultMCC,
  } = props;

  useEffect(() => {
    const hasBusinessURL = values?.businessProfileURL;
    if (!hasBusinessURL && currentUserId) {
      const pathToProfilePage = uuid =>
        createResourceLocatorString('ProfilePage', routeConfiguration, { id: uuid }, {});
      const hasMarketplaceRootURL = !!marketplaceRootURL;
      const rootUrl = hasMarketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : null;
      const profilePageURL =
        hasMarketplaceRootURL && !rootUrl.includes('localhost')
          ? `${rootUrl}${pathToProfilePage(currentUserId.uuid)}`
          : `https://test-marketplace.com${pathToProfilePage(currentUserId.uuid)}`;
      const defaultBusinessURL = `${profilePageURL}?mode=storefront`;
      formApi.change('businessProfileURL', defaultBusinessURL);
    }

    const hasMCC = values?.businessProfileMCC;
    if (!hasMCC && defaultMCC) {
      formApi.change('businessProfileMCC', defaultMCC);
    }
  }, [values?.businessProfileURL, values?.businessProfileMCC, currentUserId, defaultMCC]);

  const { dialog, handleClose, createDialogClickHandler } = useDialog();
  const countryOptions = getCountryOptions(
    intl,
    payoutCountryCodes,
    stripeCountries,
    markStripeCountries
  );

  return (
    <div className={css.sectionContainer}>
      <H4 as="h3">
        <FormattedMessage id="StripeConnectAccountForm.accountTypeTitle" />
        <a className={css.infoIon} onClick={createDialogClickHandler('accountType')}>
          <HelpOutlineIcon fontSize="small" />
        </a>
      </H4>
      <div className={css.radioButtonRow}>
        <FieldRadioButton
          id="individual"
          name="accountType"
          label={intl.formatMessage({
            id: 'StripeConnectAccountForm.individualAccount',
          })}
          value="individual"
          showAsRequired={showAsRequired}
        />
        <FieldRadioButton
          id="company"
          name="accountType"
          label={intl.formatMessage({ id: 'StripeConnectAccountForm.companyAccount' })}
          value="company"
          showAsRequired={showAsRequired}
        />
      </div>

      <FieldHidden name="businessProfileURL" />
      <FieldHidden name="businessProfileMCC" />

      <FieldSearchableSelect
        id="country"
        name="country"
        disabled={disabled}
        className={css.selectCountry}
        label={
          <>
            {countryLabel}
            <a className={css.infoIon} onClick={createDialogClickHandler('country')}>
              <HelpOutlineIcon fontSize="small" />
            </a>
          </>
        }
        options={countryOptions}
        placeholder={intl.formatMessage({ id: 'StripeConnectAccountForm.countryPlaceholder' })}
        searchPlaceholder={intl.formatMessage({
          id: 'StripeConnectAccountForm.countrySearchPlaceholder',
        })}
        validate={validators.required(
          intl.formatMessage({
            id: 'StripeConnectAccountForm.countryRequired',
          })
        )}
      />

      <FirstStepPayoutInfo stripeCountries={stripeCountries} />
      <InfoModal onClose={handleClose} dialog={dialog} />
    </div>
  );
};

const ReadonlyRow = props => {
  const { label, value } = props;
  if (!value) {
    return null;
  }
  return (
    <div className={css.readonlyRow}>
      <span className={css.summaryLabel}>{label}</span>
      <span className={css.summaryValue}>{value}</span>
    </div>
  );
};

const AlternativePayoutReadonly = props => {
  const { intl, payout, confirmationRef, stripeCountries, payoutReady } = props;
  if (!payout) {
    return null;
  }
  const showMonthlyPayoutNote = !isStripePayoutCountry(payout.country, stripeCountries);
  const readyToAcceptPayments = payoutReady && showMonthlyPayoutNote;
  const confirmationTitle = readyToAcceptPayments
    ? 'StripeConnectAccountForm.alternativeReadyTitle'
    : 'StripeConnectAccountForm.alternativeSavedTitle';
  const confirmationMessage = readyToAcceptPayments
    ? 'StripeConnectAccountForm.alternativeReadyMessage'
    : 'StripeConnectAccountForm.alternativeSavedMessage';

  const methodLabel = PAYOUT_METHOD_MESSAGE_IDS[payout.method]
    ? intl.formatMessage({ id: PAYOUT_METHOD_MESSAGE_IDS[payout.method] })
    : payout.method;

  const accounts = Array.isArray(payout.bankAccounts) ? payout.bankAccounts : [];
  const isBank = payout.method === ALTERNATIVE_PAYOUT_METHODS.BANK_TRANSFER;

  return (
    <div className={css.alternativeSection}>
      <div className={css.successBanner} role="status" tabIndex={-1} ref={confirmationRef}>
        <CheckCircleOutlineIcon fontSize="small" />
        <div>
          <p className={css.successTitle}>
            <FormattedMessage id={confirmationTitle} />
          </p>
          <p className={css.successText}>
            <FormattedMessage id={confirmationMessage} />
          </p>
        </div>
      </div>

      {showMonthlyPayoutNote ? (
        <PayoutInfoBox testId="payout-monthly-schedule">
          <FormattedMessage id="StripeConnectAccountForm.alternativeMonthlyPayoutNote" />
        </PayoutInfoBox>
      ) : null}

      <div className={css.summaryCard}>
        <span className={css.summaryLabel}>
          <FormattedMessage id="StripeConnectAccountForm.payoutMethodTitle" />
        </span>
        <span className={css.summaryValue}>{methodLabel}</span>
      </div>

      {!isBank && payout.handle ? (
        <div className={css.summaryCard}>
          <span className={css.summaryLabel}>
            <FormattedMessage
              id={
                PAYOUT_METHOD_HANDLE_LABELS[payout.method] ||
                'StripeConnectAccountForm.payoutHandleLabel'
              }
            />
          </span>
          <span className={css.summaryValue}>{payout.handle}</span>
        </div>
      ) : null}

      {isBank
        ? accounts.map((account, index) => (
            <div key={`bank-${index}`} className={css.bankAccountCard}>
              <p className={css.bankAccountTitle}>
                <FormattedMessage
                  id="StripeConnectAccountForm.bankAccountTitle"
                  values={{ number: index + 1 }}
                />
              </p>
              <ReadonlyRow
                label={intl.formatMessage({ id: 'StripeConnectAccountForm.bankNameLabel' })}
                value={account.bankName}
              />
              <ReadonlyRow
                label={intl.formatMessage({ id: 'StripeConnectAccountForm.currencyLabel' })}
                value={account.currency}
              />
              <ReadonlyRow
                label={intl.formatMessage({ id: 'StripeConnectAccountForm.fullNameLabel' })}
                value={account.fullName}
              />
              <ReadonlyRow
                label={intl.formatMessage({ id: 'StripeConnectAccountForm.fullAddressLabel' })}
                value={account.fullAddress}
              />
              <ReadonlyRow
                label={intl.formatMessage({ id: 'StripeConnectAccountForm.sortingCodeLabel' })}
                value={account.sortingCode}
              />
              <ReadonlyRow
                label={intl.formatMessage({ id: 'StripeConnectAccountForm.ibanLabel' })}
                value={account.iban}
              />
              <ReadonlyRow
                label={intl.formatMessage({ id: 'StripeConnectAccountForm.swiftLabel' })}
                value={account.swift}
              />
            </div>
          ))
        : null}

      {/* Legacy single-account shape */}
      {isBank && accounts.length === 0 && (payout.fullName || payout.accountDetails) ? (
        <div className={css.bankAccountCard}>
          <ReadonlyRow
            label={intl.formatMessage({ id: 'StripeConnectAccountForm.fullNameLabel' })}
            value={payout.fullName}
          />
          <ReadonlyRow
            label={intl.formatMessage({ id: 'StripeConnectAccountForm.fullAddressLabel' })}
            value={payout.fullAddress}
          />
          <ReadonlyRow
            label={intl.formatMessage({ id: 'StripeConnectAccountForm.ibanLabel' })}
            value={payout.accountDetails}
          />
        </div>
      ) : null}
    </div>
  );
};

const BankAccountFields = props => {
  const { name, index, intl, values, canRemove, onRemove } = props;
  const required = messageId => validators.required(intl.formatMessage({ id: messageId }));
  const extraCurrency = values?.bankAccounts?.[index]?.currency;
  const currencyOptions = getBankPayoutCurrencyOptions(intl, extraCurrency ? [extraCurrency] : []);

  // Sorting/IFSC is optional. Require IBAN/account number or SWIFT.
  const identityRequired = () => {
    const account = values?.bankAccounts?.[index] || {};
    if ((account.iban && account.iban.trim()) || (account.swift && account.swift.trim())) {
      return undefined;
    }
    return intl.formatMessage({ id: 'StripeConnectAccountForm.bankIdentityRequired' });
  };

  return (
    <div className={css.bankAccountCard}>
      <div className={css.bankAccountHeader}>
        <p className={css.bankAccountTitle}>
          <FormattedMessage
            id="StripeConnectAccountForm.bankAccountTitle"
            values={{ number: index + 1 }}
          />
        </p>
        {canRemove ? (
          <InlineTextButton
            type="button"
            className={css.removeBankAccountButton}
            onClick={onRemove}
          >
            <FormattedMessage id="StripeConnectAccountForm.removeBankAccount" />
          </InlineTextButton>
        ) : null}
      </div>

      <FieldTextInput
        id={`${name}.bankName`}
        name={`${name}.bankName`}
        type="text"
        label={intl.formatMessage({ id: 'StripeConnectAccountForm.bankNameLabel' })}
        placeholder={intl.formatMessage({ id: 'StripeConnectAccountForm.bankNamePlaceholder' })}
        validate={required('StripeConnectAccountForm.bankNameRequired')}
      />
      <FieldSelect
        id={`${name}.currency`}
        name={`${name}.currency`}
        label={intl.formatMessage({ id: 'StripeConnectAccountForm.currencyLabel' })}
        validate={required('StripeConnectAccountForm.currencyRequired')}
      >
        <option disabled value="">
          {intl.formatMessage({ id: 'StripeConnectAccountForm.currencyPlaceholder' })}
        </option>
        {currencyOptions.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </FieldSelect>
      <FieldTextInput
        id={`${name}.fullName`}
        name={`${name}.fullName`}
        type="text"
        label={intl.formatMessage({ id: 'StripeConnectAccountForm.fullNameLabel' })}
        placeholder={intl.formatMessage({
          id: 'StripeConnectAccountForm.fullNamePlaceholder',
        })}
        validate={required('StripeConnectAccountForm.fullNameRequired')}
      />
      <FieldTextInput
        id={`${name}.fullAddress`}
        name={`${name}.fullAddress`}
        type="text"
        label={intl.formatMessage({ id: 'StripeConnectAccountForm.fullAddressLabel' })}
        placeholder={intl.formatMessage({
          id: 'StripeConnectAccountForm.fullAddressPlaceholder',
        })}
        validate={required('StripeConnectAccountForm.fullAddressRequired')}
      />
      <FieldTextInput
        id={`${name}.sortingCode`}
        name={`${name}.sortingCode`}
        type="text"
        label={intl.formatMessage({ id: 'StripeConnectAccountForm.sortingCodeLabel' })}
        placeholder={intl.formatMessage({
          id: 'StripeConnectAccountForm.sortingCodePlaceholder',
        })}
        helpText={intl.formatMessage({ id: 'StripeConnectAccountForm.sortingCodeHelp' })}
      />
      <FieldTextInput
        id={`${name}.iban`}
        name={`${name}.iban`}
        type="text"
        label={intl.formatMessage({ id: 'StripeConnectAccountForm.ibanLabel' })}
        placeholder={intl.formatMessage({ id: 'StripeConnectAccountForm.ibanPlaceholder' })}
        validate={identityRequired}
      />
      <FieldTextInput
        id={`${name}.swift`}
        name={`${name}.swift`}
        type="text"
        label={intl.formatMessage({ id: 'StripeConnectAccountForm.swiftLabel' })}
        placeholder={intl.formatMessage({ id: 'StripeConnectAccountForm.swiftPlaceholder' })}
        validate={identityRequired}
      />
    </div>
  );
};

const AlternativePayoutFields = props => {
  const { intl, values } = props;
  const { dialog, handleClose, createDialogClickHandler } = useDialog();
  const selectedMethod = values?.payoutMethod;
  const showHandleField =
    selectedMethod && selectedMethod !== ALTERNATIVE_PAYOUT_METHODS.BANK_TRANSFER;
  const showBankFields = selectedMethod === ALTERNATIVE_PAYOUT_METHODS.BANK_TRANSFER;

  return (
    <div className={css.alternativeSection}>
      <div className={css.methodHeader}>
        <H4 as="h3">
          <FormattedMessage id="StripeConnectAccountForm.payoutMethodTitle" />
          <a className={css.infoIon} onClick={createDialogClickHandler('payoutMethod')}>
            <HelpOutlineIcon fontSize="small" />
          </a>
        </H4>
      </div>

      <div className={css.methodGrid} role="radiogroup">
        {ALTERNATIVE_PAYOUT_METHOD_IDS.map(method => (
          <FieldRadioButton
            key={method}
            id={`payoutMethod.${method}`}
            name="payoutMethod"
            value={method}
            variant="card"
            className={css.methodCard}
            label={
              <span className={css.methodLabel}>
                <IconPayoutMethod method={method} />
                <span>{intl.formatMessage({ id: PAYOUT_METHOD_MESSAGE_IDS[method] })}</span>
              </span>
            }
            validate={validators.required(
              intl.formatMessage({ id: 'StripeConnectAccountForm.payoutMethodRequired' })
            )}
          />
        ))}
      </div>

      {showHandleField ? (
        <FieldTextInput
          className={css.methodDetailField}
          id="payoutHandle"
          name="payoutHandle"
          type="text"
          label={intl.formatMessage({
            id:
              PAYOUT_METHOD_HANDLE_LABELS[selectedMethod] ||
              'StripeConnectAccountForm.payoutHandleLabel',
          })}
          placeholder={intl.formatMessage({
            id: PAYOUT_METHOD_HANDLE_PLACEHOLDERS[selectedMethod],
          })}
          validate={validators.required(
            intl.formatMessage({ id: 'StripeConnectAccountForm.payoutHandleRequired' })
          )}
        />
      ) : null}

      {showBankFields ? (
        <div className={css.bankTransferFields}>
          <p className={css.bankAccountsNote}>
            <FormattedMessage id="StripeConnectAccountForm.bankAccountsNote" />
          </p>
          <FieldArray name="bankAccounts">
            {({ fields }) => (
              <>
                {fields.map((name, index) => (
                  <BankAccountFields
                    key={name}
                    name={name}
                    index={index}
                    intl={intl}
                    values={values}
                    canRemove={fields.length > 1}
                    onRemove={() => fields.remove(index)}
                  />
                ))}
                <InlineTextButton
                  type="button"
                  className={css.addBankAccountLink}
                  onClick={() => fields.push({ ...EMPTY_BANK_ACCOUNT })}
                >
                  <AddIcon fontSize="small" aria-hidden="true" />
                  <FormattedMessage id="StripeConnectAccountForm.addBankAccount" />
                </InlineTextButton>
              </>
            )}
          </FieldArray>
        </div>
      ) : null}

      <InfoModal onClose={handleClose} dialog={dialog} />
    </div>
  );
};

const ErrorsMaybe = props => {
  const { stripeAccountError, stripeAccountLinkError } = props;

  const errorMessage = isStripeError(stripeAccountError) ? (
    <FormattedMessage
      id="StripeConnectAccountForm.createStripeAccountFailedWithStripeError"
      values={{ stripeMessage: stripeAccountError.apiErrors[0].meta.stripeMessage }}
    />
  ) : stripeAccountError ? (
    <FormattedMessage id="StripeConnectAccountForm.createStripeAccountFailed" />
  ) : isStripeError(stripeAccountLinkError) ? (
    <FormattedMessage
      id="StripeConnectAccountForm.createStripeAccountLinkFailedWithStripeError"
      values={{ stripeMessage: stripeAccountLinkError.apiErrors[0].meta.stripeMessage }}
    />
  ) : stripeAccountLinkError ? (
    <FormattedMessage id="StripeConnectAccountForm.createStripeAccountLinkFailed" />
  ) : null;

  return errorMessage ? <div className={css.error}>{errorMessage}</div> : null;
};

const userPayoutStarted = payout => !!(payout?.country && payout?.accountType);

/**
 * Operator mode (`?stripe=1`) is a separate Stripe Connect session.
 * The user page shows Stripe only when that user connected it. An operator
 * Stripe account does not replace the user's payout or the setup form.
 */
const resolveInitialStep = ({
  stripeConnected,
  alternativePayout,
  forceStripe,
  userChoseStripe,
}) => {
  if (forceStripe) {
    return stripeConnected ? STEP_STRIPE : STEP_SETUP;
  }
  if (userChoseStripe && stripeConnected) {
    return STEP_STRIPE;
  }
  if (userPayoutStarted(alternativePayout)) {
    return STEP_ALTERNATIVE;
  }
  return STEP_SETUP;
};

const StripeConnectAccountFormComponent = props => {
  const config = useConfiguration();
  const location = useLocation();
  const {
    onSubmit,
    currentUser,
    stripeConnected,
    savedCountry,
    savedAccountType,
    stripePayoutReady,
    forceStripe: forceStripeProp,
    ...restOfProps
  } = props;
  const stripePublishableKey = config.stripe.publishableKey;
  const supportedCountries = config.stripe.supportedCountries;
  const forceStripe = forceStripeProp === true || hasOperatorStripeOverride(location.search);

  const alternativePayout = getAlternativePayout(currentUser);
  const userChoseStripe = !forceStripe && userChoseStripePayout(currentUser);
  // Operator mode must not hydrate from, or write, the user's saved payout.
  const userPayout = forceStripe ? null : alternativePayout;
  const [step, setStep] = useState(() =>
    resolveInitialStep({
      stripeConnected,
      alternativePayout: userPayout,
      forceStripe,
      userChoseStripe,
    })
  );
  const [hasStartedOver, setHasStartedOver] = useState(false);
  const [collectingAlternative, setCollectingAlternative] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  // Keep setup answers when Fields unmount on later steps (Final Form drops them otherwise).
  const [setupSnapshot, setSetupSnapshot] = useState(() =>
    userPayout?.country
      ? {
          accountType: userPayout.accountType || 'individual',
          country: userPayout.country,
        }
      : null
  );
  const confirmationRef = useRef(null);
  const wasAlternativeSavedRef = useRef(isAlternativePayoutConfigured(currentUser));
  const payoutSignature = alternativePayout
    ? [
        alternativePayout.country,
        alternativePayout.method,
        alternativePayout.handle,
        Array.isArray(alternativePayout.bankAccounts) ? alternativePayout.bankAccounts.length : 0,
      ].join('|')
    : '';
  const payoutWhenEditingRef = useRef(payoutSignature);

  useEffect(() => {
    if (forceStripe) {
      // A non-Stripe country stays on the alternative payout flow, even when
      // an operator Stripe account already exists.
      if (step === STEP_ALTERNATIVE || hasStartedOver) {
        return;
      }
      if (stripeConnected) {
        setStep(STEP_STRIPE);
      }
      return;
    }
    if (!hasStartedOver && userChoseStripe && stripeConnected) {
      setStep(STEP_STRIPE);
      return;
    }
    if (!hasStartedOver && userPayoutStarted(alternativePayout)) {
      setSetupSnapshot({
        accountType: alternativePayout.accountType,
        country: alternativePayout.country,
      });
      setStep(STEP_ALTERNATIVE);
      return;
    }
  }, [
    stripeConnected,
    forceStripe,
    userChoseStripe,
    alternativePayout?.country,
    alternativePayout?.accountType,
    alternativePayout?.method,
    hasStartedOver,
    step,
  ]);

  useEffect(() => {
    if (!collectingAlternative) {
      return;
    }
    if (payoutSignature && payoutSignature !== payoutWhenEditingRef.current) {
      setCollectingAlternative(false);
    }
  }, [collectingAlternative, payoutSignature]);

  // Scroll to confirmation when alternative payout was just saved.
  // Offset for sticky topbar so the banner is fully visible below it.
  useEffect(() => {
    const saved = step === STEP_ALTERNATIVE && isAlternativePayoutConfigured(currentUser);
    if (saved && !wasAlternativeSavedRef.current) {
      const timer = window.setTimeout(() => {
        const el = confirmationRef.current;
        if (!el) {
          return;
        }
        const rootStyles = window.getComputedStyle(document.documentElement);
        const isDesktop = window.matchMedia('(min-width: 768px)').matches;
        const topbarHeight =
          parseInt(
            rootStyles.getPropertyValue(isDesktop ? '--topbarHeightDesktop' : '--topbarHeight'),
            10
          ) || (isDesktop ? 82 : 60);
        const extraGap = 48;
        const top = el.getBoundingClientRect().top + window.scrollY - topbarHeight - extraGap;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      }, 150);
      wasAlternativeSavedRef.current = true;
      return () => window.clearTimeout(timer);
    }
    if (!saved) {
      wasAlternativeSavedRef.current = false;
    }
    return undefined;
  }, [stripeConnected, step, currentUser, alternativePayout?.method, alternativePayout?.handle]);

  const initialBankAccounts = (() => {
    if (Array.isArray(userPayout?.bankAccounts) && userPayout.bankAccounts.length) {
      return userPayout.bankAccounts.map(account => ({
        bankName: account.bankName || '',
        currency: account.currency || '',
        fullName: account.fullName || '',
        fullAddress: account.fullAddress || '',
        sortingCode: account.sortingCode || '',
        iban: account.iban || '',
        swift: account.swift || '',
      }));
    }
    if (userPayout?.fullName || userPayout?.accountDetails) {
      return [
        {
          ...EMPTY_BANK_ACCOUNT,
          fullName: userPayout.fullName || '',
          fullAddress: userPayout.fullAddress || '',
          iban: userPayout.accountDetails || '',
        },
      ];
    }
    return [{ ...EMPTY_BANK_ACCOUNT }];
  })();

  const initialValues = {
    accountType:
      userPayout?.accountType ||
      setupSnapshot?.accountType ||
      (!hasStartedOver && userChoseStripe ? savedAccountType : null) ||
      'individual',
    country:
      userPayout?.country ||
      setupSnapshot?.country ||
      (!hasStartedOver && userChoseStripe ? savedCountry : '') ||
      '',
    payoutMethod: userPayout?.method || undefined,
    payoutHandle: userPayout?.handle || '',
    bankAccounts: initialBankAccounts,
  };

  const handleFormSubmit = (values, isUpdate) => {
    if (step === STEP_SETUP) {
      setSetupSnapshot({
        accountType: values.accountType,
        country: values.country,
      });
      if (isStripePayoutCountry(values.country, supportedCountries)) {
        setHasStartedOver(false);
        return onSubmit(
          {
            ...values,
            stripePublishableKey,
            payoutPath: 'stripe',
            recordUserPayoutChoice: !forceStripe,
          },
          isUpdate
        );
      }
      // Non-Stripe countries never touch the Stripe account, including one
      // created from the operator URL. Collect a fresh alternative payout
      // instead of showing a previously saved summary.
      payoutWhenEditingRef.current = payoutSignature;
      setCollectingAlternative(true);
      setStep(STEP_ALTERNATIVE);
      return undefined;
    }

    if (step === STEP_ALTERNATIVE) {
      const country = values.country || setupSnapshot?.country;
      const accountType = values.accountType || setupSnapshot?.accountType;
      return onSubmit(
        {
          ...values,
          country,
          accountType,
          payoutPath: 'alternative',
        },
        false
      );
    }

    return undefined;
  };

  const handleStartOver = formApi => () => {
    setHasStartedOver(true);
    setCollectingAlternative(false);
    setSetupSnapshot(null);
    setStep(STEP_SETUP);
    const nextValues = {
      accountType: 'individual',
      country: '',
      payoutMethod: undefined,
      payoutHandle: '',
      bankAccounts: [{ ...EMPTY_BANK_ACCOUNT }],
      businessProfileURL: formApi.getState()?.values?.businessProfileURL,
      businessProfileMCC: formApi.getState()?.values?.businessProfileMCC,
    };
    // Full restart clears stale validation from unmounted alternative fields.
    formApi.restart(nextValues);
    // Operator reset restarts this screen only. The user's saved payout stays.
    if (alternativePayout && !forceStripe) {
      onSubmit({ payoutPath: 'alternativeClear' }, false);
    }
  };

  return (
    <FinalForm
      {...restOfProps}
      currentUser={currentUser}
      stripeConnected={stripeConnected}
      savedCountry={savedCountry}
      savedAccountType={savedAccountType}
      initialValues={initialValues}
      enableReinitialize
      keepDirtyOnReinitialize
      destroyOnUnregister={false}
      onSubmit={values => handleFormSubmit(values, stripeConnected)}
      mutators={{
        ...arrayMutators,
      }}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          children,
          stripeAccountError,
          stripeAccountLinkError,
          disabled,
          handleSubmit,
          inProgress,
          intl,
          invalid,
          pristine,
          stripeAccountFetched,
          stripeBankAccountLastDigits,
          submitButtonText,
          form: formApi,
          values,
        } = fieldRenderProps;

        const accountDataLoaded = stripeConnected && stripeAccountFetched && savedCountry;
        const submitInProgress = inProgress;
        const countryLabel = intl.formatMessage({ id: 'StripeConnectAccountForm.countryLabel' });
        const classes = classNames(rootClassName || css.root, className, {
          [css.disabled]: disabled,
        });

        const showAsRequired = pristine;
        const currentUserId = currentUser ? currentUser.id : null;
        const activeCountry = forceStripe
          ? values?.country || setupSnapshot?.country || savedCountry
          : values?.country ||
            setupSnapshot?.country ||
            userPayout?.country ||
            (userChoseStripe ? savedCountry : '');
        const activeAccountType = forceStripe
          ? values?.accountType || setupSnapshot?.accountType || savedAccountType || 'individual'
          : values?.accountType ||
            setupSnapshot?.accountType ||
            userPayout?.accountType ||
            (userChoseStripe ? savedAccountType : null) ||
            'individual';

        const sortedPayoutCountries = getSortedPayoutCountryCodes(intl);

        const showSetup = step === STEP_SETUP;
        const showStripeStep = step === STEP_STRIPE;
        const showAlternativeStep = step === STEP_ALTERNATIVE;
        const showAlternativeSaved =
          showAlternativeStep &&
          !collectingAlternative &&
          isAlternativePayoutConfigured(currentUser);

        // Setup CTA must not depend on `invalid` — leftover alt-step field errors
        // would keep it disabled after start over.
        const submitDisabled =
          submitInProgress ||
          disabled ||
          (showSetup && (!values?.accountType || !values?.country)) ||
          (showAlternativeStep && !showAlternativeSaved && (invalid || !values?.payoutMethod));

        let computedSubmitText = submitButtonText;
        if (showSetup) {
          computedSubmitText = intl.formatMessage({
            id: 'StripeConnectAccountForm.saveAndContinue',
          });
        } else if (showAlternativeStep) {
          computedSubmitText = intl.formatMessage({
            id: 'StripeConnectAccountForm.savePayoutDetails',
          });
        }

        const submitReady = false;

        const submitButtonMaybe =
          showSetup || (showAlternativeStep && !showAlternativeSaved) ? (
            <Button
              type="submit"
              rootClassName={css.submitButton}
              inProgress={submitInProgress}
              disabled={submitDisabled}
              ready={submitReady}
            >
              {computedSubmitText || (
                <FormattedMessage id="StripeConnectAccountForm.submitButtonText" />
              )}
            </Button>
          ) : null;

        return config.stripe.publishableKey ? (
          <Form className={classes} onSubmit={handleSubmit}>
            {/* Keep setup fields registered so values survive step changes. */}
            {!showSetup ? (
              <>
                <FieldHidden name="accountType" />
                <FieldHidden name="country" />
              </>
            ) : null}

            {showSetup ? (
              <CreateStripeAccountFields
                disabled={disabled}
                showAsRequired={showAsRequired}
                countryLabel={countryLabel}
                payoutCountryCodes={sortedPayoutCountries}
                stripeCountries={supportedCountries}
                markStripeCountries={forceStripe}
                marketplaceRootURL={config.marketplaceRootURL}
                defaultMCC={config.stripe.defaultMCC}
                currentUserId={currentUserId}
                formApi={formApi}
                values={values}
                intl={intl}
              />
            ) : null}

            {showStripeStep && (!stripeConnected || accountDataLoaded) ? (
              <>
                <SummaryCards
                  accountType={activeAccountType}
                  country={activeCountry}
                  bankAccountLastDigits={stripeBankAccountLastDigits}
                  intl={intl}
                />
                {children}
              </>
            ) : null}

            {showStripeStep && stripeConnected && !accountDataLoaded ? (
              <div className={css.savedInformation}>
                <FormattedMessage id="StripeConnectAccountForm.loadingStripeAccountData" />
              </div>
            ) : null}

            {showAlternativeStep ? (
              <>
                <SummaryCards accountType={activeAccountType} country={activeCountry} intl={intl} />
                {showAlternativeSaved ? (
                  <AlternativePayoutReadonly
                    intl={intl}
                    payout={alternativePayout}
                    confirmationRef={confirmationRef}
                    stripeCountries={supportedCountries}
                    payoutReady={stripePayoutReady}
                  />
                ) : (
                  <AlternativePayoutFields intl={intl} values={values} />
                )}
              </>
            ) : null}

            <ErrorsMaybe
              stripeAccountError={stripeAccountError}
              stripeAccountLinkError={stripeAccountLinkError}
            />

            {submitButtonMaybe}

            {showAlternativeStep ? (
              <SecondaryButton
                type="button"
                className={css.resetButton}
                sx={{ mt: '24px' }}
                onClick={() => setResetConfirmOpen(true)}
              >
                <FormattedMessage id="StripeConnectAccountForm.resetPayoutDetails" />
              </SecondaryButton>
            ) : null}

            <ResetPayoutConfirmModal
              open={resetConfirmOpen}
              onClose={() => setResetConfirmOpen(false)}
              onConfirm={() => {
                setResetConfirmOpen(false);
                handleStartOver(formApi)();
              }}
            />
          </Form>
        ) : (
          <div className={css.missingStripeKey}>
            <FormattedMessage id="StripeConnectAccountForm.missingStripeKey" />
          </div>
        );
      }}
    />
  );
};

StripeConnectAccountFormComponent.defaultProps = {
  className: null,
  currentUser: null,
  stripeAccountError: null,
  disabled: false,
  inProgress: false,
  ready: false,
  savedCountry: null,
  savedAccountType: null,
  stripeBankAccountLastDigits: null,
  submitButtonText: null,
  fieldRenderProps: null,
  forceStripe: false,
  stripePayoutReady: false,
};

StripeConnectAccountFormComponent.propTypes = {
  currentUser: propTypes.currentUser,
  className: string,
  stripeAccountError: object,
  disabled: bool,
  inProgress: bool,
  ready: bool,
  savedCountry: string,
  savedAccountType: string,
  stripeBankAccountLastDigits: string,
  stripeAccountFetched: bool.isRequired,
  submitButtonText: string,
  onSubmit: func.isRequired,
  stripeConnected: bool,
  forceStripe: bool,
  stripePayoutReady: bool,
  fieldRenderProps: shape({
    handleSubmit: func,
    invalid: bool,
    pristine: bool,
    values: object,
  }),

  // from injectIntl
  intl: intlShape.isRequired,
};

const StripeConnectAccountForm = compose(injectIntl)(StripeConnectAccountFormComponent);

export default StripeConnectAccountForm;
